import React from 'react';
import { Helmet } from 'react-helmet';
import { Page } from 'components/core/Page';
import { SizeGuideHero } from 'components/organisms/SizeGuideHero';
import {
	FAQSection,
	FeaturedContentSection,
	PSPReviewSection,
	PSPVideo,
	SizeComparisonSection,
	StorageNearYou,
} from 'components/organisms';
import {
	SIZE_GUIDE_CHIPS,
	SIZE_GUIDE_HERO_CONTENT,
	SIZE_GUIDE_METADATA,
} from 'config/sizeGuide';
import { buildMainSizeGuideSchema } from './utils/buildSizeGuideSchema';
import './SizeGuidePage.scss';
import {
	SizeGuideVideoContent,
	StorageUnitChartData,
	ReviewData,
	FAQSectionContent,
	RelatedContent,
	StepContentData,
	SizeChartData,
} from './content';
import Calculator from '../../components/Calculator/Calculator';
import { pageTypes } from 'config/pageTypes';
import StorageUnitChart from 'components/organisms/StorageUnitChart/StorageUnitChart';
import { StepContent } from 'components/molecules';
import BreadCrumbs from 'components/molecules/BreadCrumbs/BreadCrumbs';
import calculatorData from 'lib/calculator/data.json';

export function SizeGuidePage() {
	const schema = buildMainSizeGuideSchema(SIZE_GUIDE_METADATA);
	const pageSchema = JSON.stringify(schema);

	const sgpBreadCrumbs = [
		{ key: 'crumb-home', label: 'Home', href: '/' },
		{
			key: 'crumb-size-guide',
			label: 'Size Guide',
			href: '/self-storage-sizes/',
		},
	];

	const canonicalUrl = `https://www.selfstorage.com/self-storage-sizes/`;
	const heroImageUrl = `https://www.selfstorage.com/images/sgp/SGPHero/size-guide-hero.webp`;

	return (
		<Page
			className="size-guide-page"
			title={SIZE_GUIDE_METADATA.title}
			description={SIZE_GUIDE_METADATA.description}
			trackingPixel=""
		>
			<Helmet>
				<title>{SIZE_GUIDE_METADATA.title}</title>
				<link
					rel="canonical"
					href={canonicalUrl}
				/>
				<meta
					name="description"
					content={SIZE_GUIDE_METADATA.description}
				/>
				<meta
					property="og:title"
					content={SIZE_GUIDE_METADATA.title}
				/>
				<meta
					property="og:description"
					content={SIZE_GUIDE_METADATA.description}
				/>
				<meta
					property="og:url"
					content={canonicalUrl}
				/>
				<meta
					property="og:type"
					content="website"
				/>
				<meta
					property="og:image"
					content={heroImageUrl}
				/>
				<meta
					property="og:site_name"
					content="SelfStorage.com"
				/>
				<meta
					name="twitter:card"
					content="summary_large_image"
				/>
				<meta
					name="twitter:site"
					content="@selfstorage"
				/>
				<meta
					name="twitter:title"
					content={SIZE_GUIDE_METADATA.title}
				/>
				<meta
					name="twitter:description"
					content={SIZE_GUIDE_METADATA.description}
				/>
				<meta
					name="twitter:image"
					content={heroImageUrl}
				/>
			</Helmet>
			<div className="page-container">
				<BreadCrumbs pspSgpBreadCrumbs={sgpBreadCrumbs} />
			</div>
			<SizeGuideHero
				subtitle={SIZE_GUIDE_HERO_CONTENT.subtitle}
				title={SIZE_GUIDE_HERO_CONTENT.title}
				description={SIZE_GUIDE_HERO_CONTENT.description}
				chips={SIZE_GUIDE_CHIPS}
			/>

			<div className="calculator-section">
				<Calculator
					siteKey="SSDC"
					data={calculatorData.SSDC}
				/>
			</div>

			<div className="video-section blue-gradient">
				<PSPVideo
					pageType={pageTypes.SGP}
					content={SizeGuideVideoContent}
				/>
			</div>

			{Object.keys(StorageUnitChartData).map((chartKey) => (
				<div
					className="sgp-section units-chart-section"
					key={chartKey}
				>
					<StorageUnitChart data={StorageUnitChartData[chartKey]} />
				</div>
			))}

			<div className="sgp-section main-page-markdown">
				<SizeComparisonSection
					content={SizeChartData}
					storageData={SizeChartData.SizeComparisonData}
				/>
			</div>

			<div className="sgp-section blue-gradient-full">
				<StepContent {...StepContentData} />
			</div>

			<div className="sgp-section reviews-section lightestBlue">
				<PSPReviewSection
					pageType={pageTypes.SGP}
					reviewsData={ReviewData}
				/>
			</div>

			<div className="sgp-section">
				<FAQSection
					pageType={pageTypes.SGP}
					content={FAQSectionContent}
				></FAQSection>
			</div>

			<div className="sgp-section resources-section">
				<FeaturedContentSection
					pageType={pageTypes.SGP}
					data={RelatedContent}
				/>
			</div>

			<div className="sgp-section storage-near-you-section">
				<StorageNearYou type="StorageSize" />
			</div>

			<script
				type="application/ld+json"
				 
				dangerouslySetInnerHTML={{ __html: pageSchema }}
			/>
		</Page>
	);
}

export default SizeGuidePage;
